import React, { useRef, useEffect, useState } from 'react';
import Globe from 'react-globe.gl';

interface Location {
  id: string;
  city: string;
  lat: number;
  lng: number;
  color?: string;
}

const CLIENT_LOCATIONS: Location[] = [
  { id: 'sa', city: 'South Africa', lat: -30.5595, lng: 22.9375, color: 'gold' },
  { id: 'angola', city: 'Angola', lat: -11.2027, lng: 17.8739, color: 'gold' },
  { id: 'nz', city: 'New Zealand', lat: -40.9006, lng: 174.886, color: 'gold' },
  { id: 'japan', city: 'Japan', lat: 36.2048, lng: 138.2529, color: 'gold' },
  { id: 'uk', city: 'United Kingdom', lat: 55.3781, lng: -3.436, color: 'gold' },
  { id: 'dubai', city: 'Dubai', lat: 25.276987, lng: 55.296249, color: 'gold' },
];

interface CountryFeature {
  properties: {
    ADMIN: string;
    [key: string]: any;
  };
  [key: string]: any;
}

export const ClientsGlobe = () => {
  const globeEl = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState({ width: 600, height: 600 });
  const [countryPolygons, setCountryPolygons] = useState<CountryFeature[]>([]);
  const [hoveredPolygon, setHoveredPolygon] = useState<CountryFeature | null>(null);

  const MIN_ZOOM = 0.5;
  const MAX_ZOOM = 5;

  useEffect(() => {
    fetch('https://raw.githubusercontent.com/holtzy/D3-graph-gallery/master/DATA/world.geojson')
      .then((res) => res.json())
      .then((data) => setCountryPolygons(data.features));
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current) {
        const { clientWidth } = containerRef.current;
        setDimensions({
          width: clientWidth,
          height: Math.min(700, clientWidth),
        });
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (globeEl.current) {
      const controls = globeEl.current.controls();
      controls.autoRotate = true;
      controls.autoRotateSpeed = 0.5;
      controls.enableZoom = false;
      controls.enablePan = false; // optional, if you want to disable panning
      controls.enableRotate = true;
      controls.enableDamping = true;
      controls.zoomSpeed = 0.5;
      controls.enableKeys = false;
      controls.mouseButtons.wheel = null; // ✅ Disable scroll zoom
    }
  }, [dimensions]);

  const handleZoom = (direction: 'in' | 'out') => {
    const camera = globeEl.current?.camera();
    if (!camera) return;

    const currentZoom = camera.zoom;
    const zoomDelta = 0.5;

    if (direction === 'in' && currentZoom < MAX_ZOOM) {
      camera.zoom = Math.min(MAX_ZOOM, currentZoom + zoomDelta);
    } else if (direction === 'out' && currentZoom > MIN_ZOOM) {
      camera.zoom = Math.max(MIN_ZOOM, currentZoom - zoomDelta);
    }

    camera.updateProjectionMatrix();
  };

  return (
    <div ref={containerRef} className="relative w-full h-full bg-white">
      <div className="absolute top-4 right-4 z-10 flex flex-col space-y-2">
        {/* Zoom In */}
        <button
          onClick={() => handleZoom('in')}
          className="bg-white p-2 rounded-full shadow hover:bg-xencode-gold transition"
          aria-label="Zoom In"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="w-5 h-5 text-black"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
        </button>

        {/* Zoom Out */}
        <button
          onClick={() => handleZoom('out')}
          className="bg-white p-2 rounded-full shadow hover:bg-xencode-gold transition"
          aria-label="Zoom Out"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="w-5 h-5 text-black"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
          </svg>
        </button>
      </div>

      {/* Globe */}
      <Globe
        ref={globeEl}
        width={dimensions.width}
        height={dimensions.height}
        globeImageUrl="https://unpkg.com/three-globe/example/img/earth-dark.jpg"
        backgroundImageUrl=""
        backgroundColor="black"
        showAtmosphere
        atmosphereColor="gold"
        atmosphereAltitude={0.3}
        pointsData={CLIENT_LOCATIONS}
        pointLat="lat"
        pointLng="lng"
        pointColor={(d: Location) => d.color || 'gold'}
        pointAltitude={0.6}
        pointRadius={0.5}
        labelsData={CLIENT_LOCATIONS}
        labelLat="lat"
        labelLng="lng"
        labelText="city"
        labelSize={1.5}
        labelDotRadius={1.2}
        labelColor={() => 'white'}
        polygonsData={countryPolygons}
        polygonCapColor={() => 'rgba(255, 215, 0, 0.02)'}
        polygonSideColor={() => 'rgba(255, 215, 0, 0.01)'}
        polygonStrokeColor={() => '#FFD700'}
        polygonAltitude={(d: CountryFeature) => (d === hoveredPolygon ? 0.0002 : 0.0001)}
        onPolygonHover={setHoveredPolygon}
        polygonLabel={(d: CountryFeature) => d.properties.ADMIN}
      />
    </div>
  );
};
